/**
 * Singleflight batching for `calendar_events/trigger_fetch`.
 *
 * Per-(userId, week) React Query rows still address the cache, but the
 * underlying HTTP call coalesces overlapping requests across users
 * sharing the same `(week, timezone, atsJobId)`. When the wizard lands
 * with N interviewers preselected (plan prefill / edit mode), all N
 * queryFns enter the batch synchronously and one trigger_fetch ships
 * for the whole roster — replacing the previous N parallel POSTs.
 *
 * Behaviours:
 *   - **Pre-fire window** — peers arriving inside `setTimeout(0)`
 *     join the same batch.
 *   - **In-flight supersede** — a new joiner arriving while the batch
 *     is already in flight aborts the in-flight HTTP call and refires
 *     with the combined roster. The original joiners' promises are
 *     preserved across the supersede (their queryFns don't re-run).
 *     Matches the legacy `KInterviewScheduler` behaviour where adding
 *     an interviewer mid-fetch cancelled the in-flight poll and
 *     restarted with the combined set.
 *   - **Per-joiner abort** — a caller-supplied `signal` aborts only
 *     that user's slice; the shared HTTP call stays in flight for the
 *     remaining joiners. When every joiner has bailed, the shared
 *     call aborts and the entry is dropped.
 */
import {
  fetchCalendarEventsWithPolling,
  type CalendarEvent,
  type CalendarEventsFetchResult,
  type CalendarUserInfo,
} from './calendarEventsApi';

export interface BatchedFetchArgs {
  userId: number;
  weekStartKey: string;
  startDate: string;
  endDate: string;
  timezone: string;
  atsJobId?: number | null;
  signal?: AbortSignal;
}

interface BatchEntry {
  /** Per-user promises waiting on the shared fetch. */
  pending: Map<
    number,
    {
      resolve: (_r: CalendarEventsFetchResult) => void;
      reject: (_e: unknown) => void;
    }
  >;
  /**
   * Aborts the currently in-flight call. Swapped on supersede so the
   * stale fetch's resolution can detect "I'm no longer the live one".
   */
  controller: AbortController;
  startDate: string;
  endDate: string;
  timezone: string;
  atsJobId?: number | null;
  /** `true` once the window has closed and an HTTP call has shipped. */
  fired: boolean;
  /** Pending `setTimeout` handle for the pre-fire window. */
  fireTimer: ReturnType<typeof setTimeout> | null;
}

const inFlightBatches = new Map<string, BatchEntry>();

function batchKey(
  weekStartKey: string,
  timezone: string,
  atsJobId: number | null
): string {
  return `${weekStartKey}|${timezone}|${atsJobId ?? ''}`;
}

function partitionEventsByUser(
  events: CalendarEvent[],
  userIds: number[]
): Map<number, CalendarEvent[]> {
  const out = new Map<number, CalendarEvent[]>();
  for (const uid of userIds) {
    out.set(uid, []);
  }
  for (const evt of events) {
    const uid = typeof evt.user_id === 'number' ? evt.user_id : null;
    if (uid !== null && out.has(uid)) {
      out.get(uid)!.push(evt);
    }
  }
  return out;
}

function pickUsersInfoForUser(
  all: CalendarUserInfo[],
  userId: number
): CalendarUserInfo[] {
  return all.filter((u) => {
    const id = typeof u.id === 'number' ? u.id : Number(u.id);
    return Number.isFinite(id) && id === userId;
  });
}

function scheduleFire(key: string, entry: BatchEntry): void {
  if (entry.fireTimer !== null || entry.fired) {
    return;
  }
  entry.fireTimer = setTimeout(() => {
    entry.fireTimer = null;
    void fireBatch(key, entry);
  }, 0);
}

/**
 * Fetch this user's week of busy events, joining any concurrent fetches
 * for the same `(week, tz, jobKey)`.
 *
 * Caller-supplied `signal` aborts only THIS user's slice; the shared
 * request stays in flight for remaining joiners.
 */
export function fetchCalendarEventsForUserBatched(
  args: BatchedFetchArgs
): Promise<CalendarEventsFetchResult> {
  const jobKey =
    args.atsJobId !== undefined && args.atsJobId !== null
      ? args.atsJobId
      : null;
  const key = batchKey(args.weekStartKey, args.timezone, jobKey);

  if (args.signal?.aborted) {
    return Promise.reject(new DOMException('Aborted', 'AbortError'));
  }

  let entry = inFlightBatches.get(key);

  if (entry && entry.fired) {
    // Supersede: a fresh joiner arrived after we already shipped the
    // HTTP request. Abort the in-flight call and re-fire with the
    // expanded roster. Existing joiners' promises stay attached to
    // this entry; they will resolve with the next response instead.
    entry.controller.abort();
    entry.controller = new AbortController();
    entry.fired = false;
  }

  if (!entry) {
    entry = {
      pending: new Map(),
      controller: new AbortController(),
      startDate: args.startDate,
      endDate: args.endDate,
      timezone: args.timezone,
      atsJobId: jobKey,
      fired: false,
      fireTimer: null,
    };
    inFlightBatches.set(key, entry);
  }

  const batch = entry;
  scheduleFire(key, batch);

  return new Promise<CalendarEventsFetchResult>((resolve, reject) => {
    batch.pending.set(args.userId, { resolve, reject });

    if (args.signal) {
      const onAbort = (): void => {
        const pending = batch.pending.get(args.userId);
        if (pending) {
          batch.pending.delete(args.userId);
          pending.reject(new DOMException('Aborted', 'AbortError'));
        }
        // If we're the last joiner, abort the shared call and drop the
        // entry. Otherwise the batch continues for the remaining users.
        if (batch.pending.size === 0) {
          batch.controller.abort();
          if (inFlightBatches.get(key) === batch) {
            inFlightBatches.delete(key);
          }
        }
      };
      if (args.signal.aborted) {
        onAbort();
      } else {
        args.signal.addEventListener('abort', onAbort, { once: true });
      }
    }
  });
}

async function fireBatch(key: string, batch: BatchEntry): Promise<void> {
  if (batch.fired) {
    return;
  }
  batch.fired = true;

  const userIds = Array.from(batch.pending.keys());
  if (userIds.length === 0) {
    if (inFlightBatches.get(key) === batch) {
      inFlightBatches.delete(key);
    }
    return;
  }

  // Capture identity so we can detect supersede on resolution.
  const myController = batch.controller;

  try {
    const res = await fetchCalendarEventsWithPolling(
      {
        userIds,
        startDate: batch.startDate,
        endDate: batch.endDate,
        timezone: batch.timezone,
        ...(batch.atsJobId !== null && batch.atsJobId !== undefined
          ? { atsJobId: batch.atsJobId }
          : {}),
      },
      { signal: myController.signal }
    );
    if (batch.controller !== myController) {
      // Superseded — a newer fire is responsible for delivering data
      // to the (possibly expanded) pending set. Drop this response.
      return;
    }
    const eventsByUser = partitionEventsByUser(res.events, userIds);
    for (const [uid, handlers] of batch.pending.entries()) {
      handlers.resolve({
        events: eventsByUser.get(uid) ?? [],
        usersInfo: pickUsersInfoForUser(res.usersInfo, uid),
      });
    }
    batch.pending.clear();
    if (inFlightBatches.get(key) === batch) {
      inFlightBatches.delete(key);
    }
  } catch (err) {
    if (batch.controller !== myController) {
      // Supersede aborted us; a newer fire is en route.
      return;
    }
    for (const { reject } of batch.pending.values()) {
      reject(err);
    }
    batch.pending.clear();
    if (inFlightBatches.get(key) === batch) {
      inFlightBatches.delete(key);
    }
  }
}

/** Test-only helper — clears the batching state between runs. */
export function __resetBatchedCalendarFetchForTests(): void {
  for (const entry of inFlightBatches.values()) {
    if (entry.fireTimer !== null) {
      clearTimeout(entry.fireTimer);
    }
    entry.controller.abort();
    for (const { reject } of entry.pending.values()) {
      reject(new DOMException('Aborted', 'AbortError'));
    }
  }
  inFlightBatches.clear();
}
